import { Hidden } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LabelAndValue } from '../../../global/components/data-display/label-value';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { GuidedProcessSupportBlock } from '../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { PageHeader } from '../../../global/components/layout/page-header';
import { SideBarContent } from '../../../global/components/layout/sidebar-content';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { getPodsContent } from '../../../global/ducks/epi-api';
import { getCurrencyFormat } from '../../../global/helpers/format-helpers';
import { replaceAll } from '../../../global/helpers/string-helpers';
import { TcsmStates } from '../../../root/tcsm-model';
import { LifeData, LifeTypes, MyCpgPodsSettings, PodContent } from '../mycpg-pod-models';


interface Props {
	settings: MyCpgPodsSettings;
}
export function LifePlanDetail(props: Props) {

	const { settings } = props;
	const lifePlans = useSelector<TcsmStates, LifeData>(state => state?.mycpgPodsState?.pods?.life_data);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState.clientGuid);
	const dispatch = useDispatch();
	const life = lifePlans?.data;
	const pensionLife = lifePlans?.pension_life_data;
	const [content, setContent] = useState<any>({});
	const [planDetail, setPlanDetail] = useState<any>({});
	const [open, setOpen] = React.useState(false);
	const [selectedPlanDetail, setSelectedItem] = React.useState('');
	const contentTemp = {};
	const planDetailTemp = {};

	const onContentSuccess = (key, html) => {
		contentTemp[key] = html;
		setContent({ ...contentTemp });
	};

	const onPlanDetailSuccess = (key, html) => {
		planDetailTemp[key] = html;
		setPlanDetail({ ...planDetailTemp });
	};

	useEffect(() => {
		life?.map(a => dispatch(getPodsContent(a?.content_path, (data) => onContentSuccess(a?.content_path, data?.contentHtml))));
		pensionLife?.map(a => dispatch(getPodsContent(a?.content_path, (data) => onContentSuccess(a?.content_path, data?.contentHtml))));
		life?.map(a => dispatch(getPodsContent(a?.plan_details, (data) => onPlanDetailSuccess(a?.plan_details, data?.contentHtml))));
		pensionLife?.map(a => dispatch(getPodsContent(a?.plan_details, (data) => onPlanDetailSuccess(a?.plan_details, data?.contentHtml))));
	}, []);


	const handleClose = () => {
		setOpen(false);
	};


	const onPlanDetailClick = (plan: string) => {
		setSelectedItem(plan);
		setOpen(true);
	};

	const renderBeneficiaries = (beneficiaries: any[], type: string) => {

		if (type === LifeTypes.LIFEGROUP) {
			return (
				<ContentSpacer>
					<p><strong>{settings?.beneficiariesTitle}: {settings?.beneficiariesStatement}</strong></p>
				</ContentSpacer>);
		}

		if (beneficiaries?.length > 0) {
			return (
				<ContentSpacer>
					<p className='h3-styled'>{settings?.beneficiariesTitle}</p>
					{
						beneficiaries?.map(b => <p>{b?.display_name} ({b?.rider}/{b?.designation_type})</p>)
					}
				</ContentSpacer>);
		}

		return (
			<ContentSpacer>
				<p><strong>{settings?.beneficiariesTitle}: {settings?.noBeneficiaries}</strong></p>
			</ContentSpacer>);
	};

	const renderLifePlan = (l: any, isPensionLife?: boolean) => {

		if (settings?.enableFtj && l?.ftj_link !== undefined)
			return (
				<>
					<ContentSpacer>
						<p className='grey-caption'>{replaceAll(settings?.policyNumberLabel, '{policy#}', l?.policy_id)}</p>
					</ContentSpacer>
					<ContentSpacer>
						<WysiwygWithReactLink content={l?.plan_description} />
					</ContentSpacer>
					<ContentSpacer>
						<p><ExternalLink linkUrl={replaceAll(settings?.lifePlanFtjRedirect, '{guid}', guid)} linkText={settings?.lifePlanFtjLinkText} /></p>
					</ContentSpacer>
				</>
			);

		if (l?.product_type === 'I' && !settings?.enableFtj)
			return (
				<ContentSpacer>
					<WysiwygWithReactLink content={settings?.newIndividualLifePlanMessage} />
				</ContentSpacer>
			);


		return (
			isPensionLife ?
				<>
					<ContentSpacer>
						<p className='h3-styled'>{getCurrencyFormat(l?.coverage_amount)}</p>
						<p className='grey-caption'>{replaceAll(settings?.benefitCoverageAmountLabel, '{PlanType}', l?.policy_name)}</p>
					</ContentSpacer>
					<ContentSpacer>
						<WysiwygWithReactLink content={content[l?.content_path]} />
					</ContentSpacer>
					{renderBeneficiaries(l?.beneficiaries, null)}
					{
						l?.plan_details &&
						<button className='link-button' type='button' onClick={() => onPlanDetailClick(l?.plan_details)}>{settings?.planDetailButtonLabel}</button>
					}
				</>
				:
				<>
					<ContentSpacer>
						<p className='h3-styled'>{getCurrencyFormat(l?.coverage_amount)}</p>
						<p className='grey-caption'>{replaceAll(settings?.benefitCoverageAmountLabel, '{PlanType}', l?.type)}</p>
					</ContentSpacer>
					<ContentSpacer>
						<p>{replaceAll(settings?.policyNumberLabel, '{policy#}', l?.policy_id)}</p>
					</ContentSpacer>
					<ContentSpacer>
						<WysiwygWithReactLink content={content[l?.content_path]} />
					</ContentSpacer>
					{l?.premium && <LabelAndValue label={settings?.premiumLabel} value={`${getCurrencyFormat(l?.premium)} ${l?.frequency}`} />}
					{l?.paid_through && <LabelAndValue label={settings?.paidThroughLabel} value={l?.paid_through} />}
					{l?.effective_date && <LabelAndValue label={settings?.effectiveDateLabel} tooltipMessage={settings?.effectiveDateTooltip} value={l?.effective_date} />}
					{l?.premium_paid_by && <LabelAndValue label={settings?.paidByLabel} value={l?.premium_paid_by} />}
					{renderBeneficiaries(l?.beneficiaries, l?.type)}
					{
						l?.plan_details &&
						<button className='link-button' type='button' onClick={() => onPlanDetailClick(l?.plan_details)}>{settings?.planDetailButtonLabel}</button>
					}
				</>);
	};


	const renderDetailSection = () => {

		return (
			<>
				<TcsmModal
					open={open}
					handleClose={handleClose}
				>
					<WysiwygWithReactLink content={planDetail[selectedPlanDetail]} />
				</TcsmModal>
				{
					pensionLife?.map(l =>
						<DividerSection key={l?.policy_name} sectionText={l?.policy_name}>
							{
								renderLifePlan(l, true)
							}
						</DividerSection>,
					)
				}
				{
					life?.map(l =>
						<DividerSection key={l?.policy_id} sectionText={replaceAll(settings?.insuranceLabel, '{#insurance}', l?.type)}>
							{
								renderLifePlan(l)
							}
						</DividerSection>,
					)
				}
			</>);

	};

	return (
		<>
			<PageHeader headerText={settings?.lifePlanDetailTitle} />
			<Hidden mdUp>
				<SideBarContent>
					<GuidedProcessSupportBlock title={settings?.lifeGpsTitle} contents={settings?.lifeGps} />
				</SideBarContent>
			</Hidden>
			{
				renderDetailSection()
			}
			{
				lifePlans?.content === PodContent.NOT_ENROLLED &&
				<DividerSection removeDivider>
					<WysiwygWithReactLink content={settings?.lifePlanNotEnrolledMessage} />
				</DividerSection>
			}
		</>
	);

}

